import React, { useState, useEffect, useCallback } from 'react';
import industryDataset from './data/IndustryDataset';
import "../styles/steps.css";
import { FaLaptopCode, FaHome, FaTools, FaSyncAlt, FaListOl } from "react-icons/fa";

const ImplementationType = ({ formData, updateFormData, setStepValidation, stepNumber }) => {
    const [error, setError] = useState('');

    // Validate fields when Next is clicked
    const validateFields = useCallback((nextClicked = false) => {
        let errorMessage = '';
        if (nextClicked) {
            if (!formData.implementationType) {
                errorMessage = 'Please select a project type.';
            } else if (!formData.industry) {
                errorMessage = 'Please select an industry.';
            }
        }

        setError(errorMessage);
        return errorMessage === ''; // Return true if valid, false if not
    }, [formData.implementationType, formData.industry]);


    useEffect(() => {
        if (setStepValidation) {
            setStepValidation(stepNumber, validateFields);
        }
    }, [setStepValidation, stepNumber, validateFields]); // Only include stable dependencies

    // Handle project type change
    const handleProjectTypeChange = (e) => {
        const implementationType = e.target.value;
        updateFormData({ implementationType });
    };

    // Handle industry change
    const handleIndustryChange = (e) => {
        const industry = e.target.value;
        const industryData = industryDataset[industry];

        if (industryData) {
            // Set default project type based on the industry
            const defaultProjectType = industryData.projectTypes[0]; // First project type in the array
            updateFormData({
                industry,
                implementationType: defaultProjectType,
                preferredResources: industryData.preferredResources,
                techStack: industryData.techStack,
                sprintsMilestones: industryData.sprintsOrMilestones,
                projectExpenses: industryData.projectExpenses,
            });
        } else {
            updateFormData({ industry });
        }
    };

    return (
        <div className="step-container">
            <div className="step-description">
                <p>
                    Select your project's Industry Type and Implementation Methodology (Agile or Waterfall). These choices help Soft Estimate tailor recommendations for time, cost, and resources.
                </p>

                {/* Industry Selection */}
                <div className="industry-section">
                    🌍 Industry Type : Choose from Software Development, House Remodeling, or General Contracting. Each industry has unique timelines, costs, and workflows, so selecting the right one ensures accurate estimates.
                    <ul className="industry-list">
                        <li><FaLaptopCode className="icon" /> Software Development – Web, apps, and system development.</li>
                        <li><FaHome className="icon" /> House Remodeling – Home renovations and construction.</li>
                        <li><FaTools className="icon" /> General Contracting – Flexible option for any industry.</li>
                    </ul>
                </div>

                {/* Project Type Selection */}
                <div className="methodology-section">
                    ⚙️ Implementation Methodology : Not sure which to pick? Industry trends: Agile is recommended for Software Development due to its adaptability. Waterfall is ideal for House Remodeling and General Contracting, where structured phases are essential.
                    <ul className="methodology-list">
                        <li><FaSyncAlt className="icon" /> Agile – Fast, flexible, great for evolving projects.</li>
                        <li><FaListOl className="icon" /> Waterfall – Structured, step-by-step, best for well-defined projects.</li>
                    </ul>
                </div>

                <p className="note">💡 Your selections shape your estimate. Pick what fits best!</p>
            </div>
            <br></br>
            <div className="input-group-inOneRow">
                <label className="input-label"><strong>Industry Type:</strong></label>
                <select
                    className="select-field"
                    name="industry"
                    value={formData.industry}
                    onChange={handleIndustryChange}
                    required
                >
                    <option value="">Select an industry</option>
                    {Object.keys(industryDataset).map((industry) => (
                        <option key={industry} value={industry}>
                            {industry}
                        </option>
                    ))}
                </select>
            </div>
            <div className="input-group-inOneRow">
                <label className="input-label"><strong>Implementation Methodology:</strong></label>
                <div>
                    <label className="input-label">
                        <input
                            className="input-radio-field"
                            type="radio"
                            name="implementationType"
                            value="Agile"
                            checked={formData.implementationType === "Agile"}
                            onChange={handleProjectTypeChange}
                        />{" "}
                        Agile
                    </label>
                    <label className="input-label">
                        <input
                            className="input-radio-field"
                            type="radio"
                            name="implementationType"
                            value="Waterfall"
                            checked={formData.implementationType === "Waterfall"}
                            onChange={handleProjectTypeChange}
                        />{" "}
                        Waterfall
                    </label>
                </div>
            </div>

            {/* Show error message inside the step if validation fails */}
            {error && <p style={{ color: 'red' }}>{error}</p>}
        </div>
    );
};

export default ImplementationType;